import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, useThemedStyletron } from "@bluecrew/blueprint-web";
import { useStep } from "react-hooks-helper";
// eslint-disable-next-line no-restricted-imports
import { styled } from "baseui";
import { Toolbar } from "../Toolbar";
import { Wage } from "../types/propTypes/HourlyPayField.types";
import {
  CreatePositionScreenProps,
  FormFieldTypes,
} from "../types/propTypes/CreatePositionScreen.types";
import { SkillsetSection } from "./SkillsetSection";
import { RequirementsSection } from "./RequirementsSection";
import { PositionDetailsSection } from "./PositionDetailsSection";
import { Divider } from "../../../components/Divider";
import { ScreenHeader } from "../ScreenHeader";
import { ErrorMessage } from "../ErrorMessage";
import { useAppSelector } from "../../../redux";
import { getTreatmentValue } from "../../../redux/selectors/splitio";
import { SKILL_DRUG_TEST_REQUIREMENTS_SPLIT_NAME } from "../../../../shared/constants";
import { debouncedDrugTestRequirements } from "../../../../shared/hooks/debouncedDrugTestRequirements";

export const CreatePositionScreen = ({
  defaultValues,
  events,
  data: { skills, positions, geofence },
  config = {},
}: CreatePositionScreenProps) => {
  const { control, trigger, handleSubmit, errors, watch, setValue, getValues, formState } =
    useForm<FormFieldTypes>({
      defaultValues: {
        ...defaultValues,
        drugTestSelection: undefined,
      },
    });
  const {
    index: step,
    navigation: { next },
  } = useStep({ steps: 3, initialStep: defaultValues.skillset ? 2 : 0 });

  const onSubmit = async (formData: FormFieldTypes) => {
    try {
      setPositionCreationError(null);
      await events?.onSave?.(formData);
    } catch (error) {
      setPositionCreationError("Failed to create position. Please try again.");
    }
  };

  const [, theme] = useThemedStyletron();
  const [positionCreationError, setPositionCreationError] = useState<string | null>(null);

  const selectedSkillset = watch("skillset");
  const worksite = watch("worksite");
  const featureFlagEnabled = useAppSelector(
    (state) => getTreatmentValue(state, SKILL_DRUG_TEST_REQUIREMENTS_SPLIT_NAME) === "on",
  );

  const { drugTestRequired, exemptStates, isLoadingDrugTest } = debouncedDrugTestRequirements(
    selectedSkillset?.id ? Number(selectedSkillset.id) : undefined,
    featureFlagEnabled,
  );

  const onSkillsetNext = async () => {
    await trigger("skillset");
    if (!("skillset" in errors)) {
      next();
    }
  };

  useEffect(() => {
    if (formState.isDirty) {
      setValue(
        "requirements",
        selectedSkillset?.requirements.filter((req) => req.selected),
      );
      setValue("customRequirements", []);
    }
    setValue("generalRequirements", `***General Requirements***\n${selectedSkillset?.description}`);
  }, [selectedSkillset]);


  return (
    <>
      <ScreenHeader>Create a position</ScreenHeader>
      <FormStyledWrapper>
        <SkillsetSection
          control={control}
          trigger={trigger}
          onNext={onSkillsetNext}
          skills={skills}
        />
        {step >= 1 && (
          <>
            {selectedSkillset?.warning != null && (
              <StyledText
                $style={{ paddingBottom: "0px", paddingTop: "24px" }}
                dangerouslySetInnerHTML={{ __html: selectedSkillset.warning }}
              />
            )}
            <RequirementsSection
              control={control}
              trigger={trigger}
              requirements={selectedSkillset!.requirements}
              certifications={selectedSkillset!.certifications}
              skillsetId={Number(selectedSkillset!.id)}
              drugTestRequired={drugTestRequired}
              exemptStates={exemptStates}
              isLoadingDrugTest={isLoadingDrugTest}
              positionState={worksite?.state}
            />
            <PositionDetailsSection
              errors={errors}
              control={control}
              trigger={trigger}
              positions={positions}
              geofence={geofence}
              skill={selectedSkillset}
              onLocationSelect={async (address) => {
                if (events?.onLocationSelect) {
                  const res: Wage = await events.onLocationSelect(address);
                  // Get current wage to not rewrite the existing value in the field
                  const inputWageValue = getValues("hourlyPay")?.wage || 0;
                  setValue("hourlyPay", {
                    wage: inputWageValue,
                    minWage: res.wage,
                  });
                }
              }}
              config={config.positionDetails}
            />
          </>
        )}
        {step >= 1 && (
          <>
            <Divider $marginTop={theme.sizing.scale1000} />
            {positionCreationError && <ErrorMessage text={positionCreationError} />}
            <Toolbar $justifyContent="flex-end" style={{ paddingBottom: "80px" }}>
              <Button onClick={handleSubmit(onSubmit)}>Save position</Button>
            </Toolbar>
          </>
        )}
      </FormStyledWrapper>
    </>
  );
};

const FormStyledWrapper = styled("div", ({ $theme }) => ({
  width: "827px",
  marginLeft: "80px",
  marginTop: $theme.sizing.scale1000,
}));

const StyledText = styled("p", ({ $theme }) => ({
  ...$theme.typography.font300,
  color: $theme.colors.primary700,
  paddingBottom: $theme.sizing.scale900,
  margin: 0,
}));
